// components/Modals/QuickContentModal/QuickContentModal.js
import React, { useState, useEffect, useRef } from 'react';
import './QuickContentModal.css';
import { toast } from 'react-hot-toast';
import { contentAPI } from '../../../services/api';
import axios from 'axios';

// Tab Components
import MediaTab from '../ContentModal/tabs/MediaTab';
import SocialMediaTab from '../ContentModal/tabs/SocialMediaTab';
import AutogramSettingsTab from '../ContentModal/tabs/AutogramSettingsTab';

// Other Components
import MediaWatermark from '../ContentModal/components/MediaWatermark';
import LoadingOverlay from '../ContentModal/components/LoadingOverlay';
import BulkVideoWatermark from '../ContentModal/components/BulkVideoWatermark';

function QuickContentModal({
    isOpen,
    onClose,
    tags = [],
    onSubmit,
    onContentCreated,
    API_URL: apiUrl
}) {
    // Media states
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
    const [selectedMediaForBulk, setSelectedMediaForBulk] = useState([]);

    // UI states
    const [activeTab, setActiveTab] = useState('media');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form states
    const [contentForm, setContentForm] = useState({
        title: '',
        content: '',
        media: [],
        tagIds: [],
        buttons: []
    });

    const [buttonForm, setButtonForm] = useState([]);
    const [currentButton, setCurrentButton] = useState({ text: '', url: '' });
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkData, setLinkData] = useState({ text: '', url: '' });

    // Watermark states
    const [showWatermark, setShowWatermark] = useState(false);
    const [watermarkingIndex, setWatermarkingIndex] = useState(null);

    // BULK VIDEO WATERMARK STATE
    const [showBulkWatermark, setShowBulkWatermark] = useState(false);
    const [bulkWatermarkData, setBulkWatermarkData] = useState(null);

    // Platform-specific states
    const [telegramSettings, setTelegramSettings] = useState({
        buttons: [],
        parse_mode: 'HTML',
        disable_notification: false,
        protect_content: false
    });

    const [youtubeSettings, setYoutubeSettings] = useState({
        title: '',
        description: '',
        tags: [],
        privacy: 'private',
        categoryId: '22',
        shorts: true
    });

    const [xSettings, setXSettings] = useState({
        text: '',
        useOriginal: false,
        charCount: 0
    });

    const [tiktokSettings, setTiktokSettings] = useState({
        caption: '',
        hashtags: '',
        allowComments: true,
        allowDuet: true
    });

    const [instagramSettings, setInstagramSettings] = useState({
        contentType: 'post',
        caption: '',
        firstComment: '',
        collaborators: '',
        userTags: ''
    });

    const [platformSettings, setPlatformSettings] = useState({
        telegram: true,
        youtube: false,
        x: false,
        tiktok: false,
        instagram: false
    });

    // Saved settings state
    const [savedSettings, setSavedSettings] = useState(null);
    const [showSavedSettings, setShowSavedSettings] = useState(false);

    // Debug için tags'leri kontrol et
    useEffect(() => {
        console.log('QuickContentModal - Gelen tags:', tags);
        console.log('Tags length:', tags?.length);
    }, [tags]);

    // Load saved settings from backend
    useEffect(() => {
        const loadSettings = async () => {
            if (!isOpen || !apiUrl) return;

            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiUrl}/quick-content-settings`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.settings) {
                        setSavedSettings(data.settings);
                        applySettings(data.settings);
                    }
                }
            } catch (error) {
                console.error('Ayarlar yüklenemedi:', error);
            }
        };

        loadSettings();
    }, [isOpen, apiUrl]);

    // Apply settings helper function
    const applySettings = (settings) => {
        if (!settings) return;

        setContentForm(prev => ({
            ...prev,
            title: settings.title || '',
            content: settings.content || ''
        }));
        setSelectedTagIds(settings.tagIds || []);
        const platforms = settings.platformSettings || {};
        setPlatformSettings({
            telegram: platforms.telegram !== false,
            youtube: platforms.youtube || false,
            x: platforms.x || false,
            tiktok: platforms.tiktok || false,
            instagram: platforms.instagram || false
        });
        setTelegramSettings(settings.telegramSettings || {
            buttons: settings.buttons || [],
            parse_mode: 'HTML',
            disable_notification: false,
            protect_content: false
        });
        setYoutubeSettings(settings.youtubeSettings || {
            title: '',
            description: '',
            tags: [],
            privacy: 'private',
            categoryId: '22',
            shorts: true
        });
        setXSettings(settings.xSettings || { text: '', useOriginal: false, charCount: 0 });
        setTiktokSettings(settings.tiktokSettings || { caption: '', hashtags: '', allowComments: true, allowDuet: true });
        setInstagramSettings(settings.instagramSettings || { contentType: 'post', caption: '', firstComment: '', collaborators: '', userTags: '' });

        setButtonForm(settings.buttons || []);
    };

    // Save settings to backend
    const saveCurrentSettings = async () => {
        if (!apiUrl) {
            console.error('API_URL tanımlı değil');
            return;
        }

        const settings = {
            title: contentForm.title,
            content: contentForm.content,
            tagIds: selectedTagIds,
            platformSettings,
            telegramSettings,
            youtubeSettings,
            xSettings,              // ← EKLENDİ
            tiktokSettings,         // ← EKLENDİ
            instagramSettings,      // ← EKLENDİ
            buttons: buttonForm,
            savedAt: new Date().toISOString()
        };

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${apiUrl}/quick-content-settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ settings })
            });

            if (response.ok) {
                setSavedSettings(settings);
                toast.success('Ayarlar tüm kullanıcılar için kaydedildi!');
            } else {
                throw new Error('Backend kayıt hatası');
            }
        } catch (error) {
            console.error('Ayarlar backend\'e kaydedilemedi:', error);
            toast.error('Ayarlar kaydedilemedi!');
        }
    };

    // Clear saved settings from backend
    const clearSavedSettings = async () => {
        if (!apiUrl) {
            console.error('API_URL tanımlı değil');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await fetch(`${apiUrl}/quick-content-settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ settings: null })
            });

            setSavedSettings(null);
            setContentForm({
                title: '',
                content: '',
                media: [],
                tagIds: [],
                buttons: []
            });
            setSelectedTagIds([]);
            setButtonForm([]);
            setPlatformSettings({
                telegram: true,
                youtube: false,
                x: false,
                tiktok: false,
                instagram: false
            });

            toast.success('Kaydedilmiş ayarlar temizlendi!');
        } catch (error) {
            console.error('Ayarlar temizlenemedi:', error);
            toast.error('Ayarlar temizlenemedi!');
        }
    };

    // Toggle media selection for bulk
    const toggleMediaSelection = (index) => {
        if (selectedMediaForBulk.includes(index)) {
            setSelectedMediaForBulk(selectedMediaForBulk.filter(i => i !== index));
        } else {
            setSelectedMediaForBulk([...selectedMediaForBulk, index]);
        }
    };

    // Select all media
    const selectAllMedia = () => {
        if (selectedMediaForBulk.length === mediaPreviews.length) {
            setSelectedMediaForBulk([]);
        } else {
            setSelectedMediaForBulk(mediaPreviews.map((_, index) => index));
        }
    };

    // Bulk Watermark Handler
    const handleBulkWatermarkSave = (watermarkData) => {
        setBulkWatermarkData(watermarkData);

        // Seçili videolara watermark badge ekle
        const newPreviews = mediaPreviews.map((preview, index) => {
            if (selectedMediaForBulk.includes(index) && preview.type === 'video') {
                return {
                    ...preview,
                    hasWatermark: true,
                    watermarkData: watermarkData
                };
            }
            return preview;
        });

        setMediaPreviews(newPreviews);

        const videoCount = selectedMediaForBulk.filter(idx => mediaPreviews[idx]?.type === 'video').length;
        toast.success(`🎬 ${videoCount} videoya logo ayarlandı!`);
    };

    // Clear Bulk Watermark
    const clearBulkWatermark = () => {
        setBulkWatermarkData(null);

        const newPreviews = mediaPreviews.map(preview => ({
            ...preview,
            hasWatermark: false,
            watermarkData: null
        }));

        setMediaPreviews(newPreviews);
        toast.success('Logo ayarları temizlendi');
    };

    // Get video count in selected media
    const getSelectedVideoCount = () => {
        if (selectedMediaForBulk.length > 0) {
            return selectedMediaForBulk.filter(idx => mediaPreviews[idx]?.type === 'video').length;
        }
        return mediaPreviews.filter(p => p.type === 'video').length;
    };

    // Custom MediaTab for bulk selection
    const renderBulkMediaTab = () => {
        const selectedVideoCount = getSelectedVideoCount();

        return (
            <div className="tab-content animate-fadeIn">
                {/* Medya Yükleme Alanı - Her zaman göster */}
                <MediaTab
                    mediaPreviews={mediaPreviews}
                    setMediaPreviews={setMediaPreviews}
                    mediaFiles={mediaFiles}
                    setMediaFiles={setMediaFiles}
                    contentForm={contentForm}
                    setContentForm={setContentForm}
                    selectedMediaIndex={selectedMediaIndex}
                    setSelectedMediaIndex={setSelectedMediaIndex}
                    handleOpenWatermark={handleOpenWatermark}
                />

                {/* Medya yüklendiyse seçim ve watermark alanını göster */}
                {mediaPreviews.length > 0 && (
                    <>
                        {/* Bulk Mode Header */}
                        <div className="bulk-mode-header" style={{ marginTop: '20px' }}>
                            <div className="bulk-selection-info">
                                <span>{selectedMediaForBulk.length} / {mediaPreviews.length} medya seçili</span>
                                <button
                                    type="button"
                                    onClick={selectAllMedia}
                                    className="btn-select-all"
                                >
                                    {selectedMediaForBulk.length === mediaPreviews.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                                </button>
                            </div>

                            {/* Bulk Video Watermark Button */}
                            {selectedVideoCount > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            console.log('🎬 Bulk Watermark butonu tıklandı, selectedVideoCount:', selectedVideoCount);
                                            setShowBulkWatermark(true);
                                        }}
                                        className="btn-bulk-watermark"
                                    >
                                        <span>🎬</span>
                                        Videolara Logo Ekle
                                        <span className="watermark-count">{selectedVideoCount}</span>
                                    </button>

                                    {bulkWatermarkData && (
                                        <button
                                            type="button"
                                            onClick={clearBulkWatermark}
                                            className="btn-clear-watermark"
                                        >
                                            <span>🗑️</span>
                                            Logoyu Kaldır
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Watermark Info Bar */}
                        {bulkWatermarkData && (
                            <div className="watermark-info-bar">
                                <div className="info-left">
                                    <img
                                        src={bulkWatermarkData.preview}
                                        alt="Logo"
                                        className="info-logo"
                                    />
                                    <div className="info-text">
                                        <strong>Logo ayarlandı:</strong> {getSelectedVideoCount()} video •
                                        Pozisyon: {bulkWatermarkData.position} •
                                        Opaklık: %{bulkWatermarkData.opacity}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowBulkWatermark(true)}
                                    style={{
                                        background: 'rgba(255,255,255,0.1)',
                                        border: 'none',
                                        color: '#fff',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                >
                                    Düzenle
                                </button>
                            </div>
                        )}

                        {/* Bulk Media Grid - Seçim için */}
                        <div className="bulk-media-grid">
                            {mediaPreviews.map((preview, index) => (
                                <div
                                    key={index}
                                    className={`bulk-media-item ${selectedMediaForBulk.includes(index) ? 'selected' : ''}`}
                                    onClick={() => toggleMediaSelection(index)}
                                >
                                    <div className="bulk-media-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedMediaForBulk.includes(index)}
                                            onChange={() => { }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>

                                    {/* Watermark Badge */}
                                    {preview.hasWatermark && preview.type === 'video' && (
                                        <div className="watermark-badge">
                                            <span>🏷️</span> Logo
                                        </div>
                                    )}

                                    {preview.type === 'image' ? (
                                        <img
                                            src={preview.url}
                                            alt={`Media ${index + 1}`}
                                            className="bulk-media-preview"
                                        />
                                    ) : (
                                        <div className="bulk-media-video">
                                            <span className="video-icon">📹</span>
                                            <span className="video-name">{preview.name || `Video ${index + 1}`}</span>
                                        </div>
                                    )}
                                    <div className="bulk-media-number">{index + 1}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Clean up on modal close
    useEffect(() => {
        if (!isOpen) {
            mediaPreviews.forEach(preview => {
                if (preview.type === 'video' && preview.url?.startsWith('blob:')) {
                    URL.revokeObjectURL(preview.url);
                }
            });

            setMediaPreviews([]);
            setMediaFiles([]);
            setSelectedMediaForBulk([]);
            setActiveTab('media');
            setShowEmojiPicker(false);
            setCurrentButton({ text: '', url: '' });
            setSelectedTagIds([]);
            setShowLinkModal(false);
            setLinkData({ text: '', url: '' });
            setShowWatermark(false);
            setWatermarkingIndex(null);
            setShowBulkWatermark(false);
            setBulkWatermarkData(null);
            setUploadProgress(0);
            setIsUploading(false);
            setIsSubmitting(false);
            setShowSavedSettings(false);
            setPlatformSettings({
                telegram: true,
                youtube: false,
                x: false,
                tiktok: false,
                instagram: false
            });
            setXSettings({
                text: '',
                useOriginal: false,
                charCount: 0
            });
            setTiktokSettings({
                caption: '',
                hashtags: '',
                allowComments: true,
                allowDuet: true
            });
            setInstagramSettings({
                contentType: 'post',
                caption: '',
                firstComment: '',
                collaborators: '',
                userTags: ''
            });
        }
    }, [isOpen, mediaPreviews]);

    // ESC key handler
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27) {
                if (showBulkWatermark) {
                    setShowBulkWatermark(false);
                } else if (showLinkModal) {
                    setShowLinkModal(false);
                } else if (showEmojiPicker) {
                    setShowEmojiPicker(false);
                } else if (showWatermark) {
                    setShowWatermark(false);
                } else if (isOpen) {
                    onClose();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose, showEmojiPicker, showLinkModal, showWatermark, showBulkWatermark]);

    // Watermark handlers
    const handleWatermarkSave = (processedFile, processedPreview) => {
        if (watermarkingIndex !== null) {
            const newMediaFiles = [...mediaFiles];
            const newMediaPreviews = [...mediaPreviews];

            newMediaFiles[watermarkingIndex] = processedFile;
            newMediaPreviews[watermarkingIndex] = {
                ...newMediaPreviews[watermarkingIndex],
                url: processedPreview,
                watermarked: true,
                file: processedFile
            };

            setMediaFiles(newMediaFiles);
            setMediaPreviews(newMediaPreviews);
            setContentForm({ ...contentForm, media: newMediaFiles });
        }

        setShowWatermark(false);
        setWatermarkingIndex(null);
        toast.success('Watermark başarıyla eklendi!');
    };

    const handleOpenWatermark = (index) => {
        const preview = mediaPreviews[index];
        if (preview && preview.type === 'image') {
            setWatermarkingIndex(index);
            setShowWatermark(true);
        }
    };

    // Bulk content creation
    const handleBulkSubmit = async () => {
        console.log('🚀 Bulk Submit başlatıldı');
        console.log('Selected Media Indices:', selectedMediaForBulk);
        console.log('Media Files:', mediaFiles);
        console.log('Media Previews:', mediaPreviews);
        console.log('Bulk Watermark Data:', bulkWatermarkData);

        if (!contentForm.title?.trim()) {
            toast.error('Başlık zorunludur');
            return;
        }

        if (selectedTagIds.length === 0) {
            toast.error('En az bir etiket seçmelisiniz');
            return;
        }

        if (selectedMediaForBulk.length === 0) {
            toast.error('Toplu oluşturma için en az bir medya seçmelisiniz');
            return;
        }

        const hasSelectedPlatform = Object.values(platformSettings).some(v => v);
        if (!hasSelectedPlatform) {
            setPlatformSettings(prev => ({ ...prev, telegram: true }));
        }

        setIsSubmitting(true);
        setUploadProgress(0);

        let successCount = 0;
        let errorCount = 0;

        try {
            const cleanTagIds = selectedTagIds.map(id => {
                if (typeof id === 'string') {
                    return parseInt(id.replace(/[\[\]"']/g, ''));
                }
                return parseInt(id);
            }).filter(id => !isNaN(id));

            for (let i = 0; i < selectedMediaForBulk.length; i++) {
                const mediaIndex = selectedMediaForBulk[i];
                const mediaFile = mediaFiles[mediaIndex];
                const mediaPreview = mediaPreviews[mediaIndex];

                console.log(`📦 İşleniyor ${i + 1}/${selectedMediaForBulk.length}:`, {
                    mediaIndex,
                    hasFile: !!mediaFile,
                    fileName: mediaFile?.name,
                    fileSize: mediaFile?.size,
                    fileType: mediaFile?.type,
                    previewType: mediaPreview?.type,
                    hasWatermark: mediaPreview?.hasWatermark
                });

                try {
                    const formData = new FormData();

                    const title = selectedMediaForBulk.length > 1
                        ? `${contentForm.title} #${i + 1}`
                        : contentForm.title;

                    formData.append('title', title);
                    formData.append('content', contentForm.content || '');
                    formData.append('tagIds', JSON.stringify(cleanTagIds));

                    if (mediaFile instanceof File) {
                        formData.append('media', mediaFile, mediaFile.name);
                        console.log('✅ Dosya eklendi:', mediaFile.name);
                    } else if (mediaPreview?.file instanceof File) {
                        formData.append('media', mediaPreview.file, mediaPreview.file.name);
                        console.log('✅ Preview dosyası eklendi:', mediaPreview.file.name);
                    } else {
                        console.error('❌ Dosya bulunamadı:', { mediaFile, mediaPreview });
                        errorCount++;
                        continue;
                    }

                    // VIDEO WATERMARK DATA - Backend'in beklediği format (apply_watermark + watermark_settings + watermark_logo)
                    if (mediaPreview?.type === 'video' && bulkWatermarkData) {
                        console.log('🎬 Video watermark verisi ekleniyor...');

                        // Multer field adı: watermarkLogo (content.routes.js satır 62)
                        formData.append('watermarkLogo', bulkWatermarkData.file, 'watermark_logo.png');

                        // Middleware videoWatermarks objesini bekliyor (content.routes.js satır 121)
                        const watermarkConfig = {
                            "0": {
                                position: bulkWatermarkData.position,
                                size: bulkWatermarkData.size,
                                opacity: bulkWatermarkData.opacity
                            }
                        };
                        formData.append('videoWatermarks', JSON.stringify(watermarkConfig));

                        console.log('✅ Watermark verisi eklendi:', watermarkConfig);
                    }

                    formData.append('platforms', JSON.stringify(platformSettings));

                    // TELEGRAM AYARLARI
                    if (platformSettings.telegram) {
                        formData.append('buttons', JSON.stringify(telegramSettings.buttons || []));
                        formData.append('telegram_settings', JSON.stringify(telegramSettings));
                    }

                    // X (TWITTER) AYARLARI 
                    if (xSettings.text) {
                        formData.append('x_text', xSettings.text);
                        formData.append('is_x_enabled', 'true');
                    }

                    // TIKTOK AYARLARI
                    if (tiktokSettings.caption || tiktokSettings.hashtags) {
                        formData.append('tiktok_caption', tiktokSettings.caption || '');
                        formData.append('tiktok_hashtags', tiktokSettings.hashtags || '');
                        formData.append('tiktok_allow_comments', tiktokSettings.allowComments !== false ? 'true' : 'false');
                        formData.append('tiktok_allow_duet', tiktokSettings.allowDuet !== false ? 'true' : 'false');
                        formData.append('is_tiktok_enabled', 'true');
                    }

                    // INSTAGRAM AYARLARI
                    if (instagramSettings.caption || instagramSettings.contentType !== 'post') {
                        formData.append('instagram_content_type', instagramSettings.contentType || 'post');
                        formData.append('instagram_caption', instagramSettings.caption || '');
                        formData.append('instagram_first_comment', instagramSettings.firstComment || '');
                        formData.append('instagram_collaborators', instagramSettings.collaborators || '');
                        formData.append('instagram_user_tags', instagramSettings.userTags || '');
                        formData.append('is_instagram_enabled', 'true');
                    }

                    // YOUTUBE AYARLARI
                    if (youtubeSettings.title || youtubeSettings.description) {
                        formData.append('youtube_title', youtubeSettings.title || title);
                        formData.append('youtube_description', youtubeSettings.description || contentForm.content || '');
                        formData.append('youtube_tags', JSON.stringify(youtubeSettings.tags || []));
                        formData.append('youtube_privacy', youtubeSettings.privacy || 'public');
                        formData.append('youtube_category_id', youtubeSettings.categoryId || '24');
                        formData.append('is_youtube_enabled', 'true');
                        formData.append('is_youtube_shorts', String(youtubeSettings.shorts));
                    }

                    console.log('📋 FormData içeriği:');
                    for (let [key, value] of formData.entries()) {
                        if (value instanceof File) {
                            console.log(`  ${key}: [File: ${value.name}, ${value.size} bytes]`);
                        } else {
                            console.log(`  ${key}:`, value);
                        }
                    }

                    console.log('📡 API çağrısı yapılıyor...');
                    const response = await contentAPI.create(formData);
                    console.log('✅ API yanıtı:', response.data);

                    successCount++;

                    setUploadProgress(Math.round(((i + 1) / selectedMediaForBulk.length) * 100));

                    // Video watermark işlemi uzun sürebilir, bekleme süresini artır
                    const waitTime = (mediaPreview?.type === 'video' && bulkWatermarkData) ? 500 : 200;
                    await new Promise(resolve => setTimeout(resolve, waitTime));

                } catch (error) {
                    console.error(`❌ Medya ${mediaIndex + 1} için hata:`, error);
                    console.error('Error response:', error.response?.data);
                    errorCount++;

                    if (error.response?.data?.error) {
                        toast.error(`Medya ${i + 1}: ${error.response.data.error}`);
                    }
                }
            }

            setIsSubmitting(false);
            setUploadProgress(100);

            if (successCount > 0 && errorCount === 0) {
                toast.success(`🎉 ${successCount} içerik başarıyla oluşturuldu!`);
            } else if (successCount > 0 && errorCount > 0) {
                toast.warning(`⚠️ ${successCount} içerik oluşturuldu, ${errorCount} başarısız`);
            } else {
                toast.error('❌ İçerik oluşturulamadı');
            }

            if (onContentCreated && successCount > 0) {
                onContentCreated(successCount);
            }

            if (successCount > 0) {
                setTimeout(() => {
                    onClose();
                }, 1000);
            }

        } catch (error) {
            console.error('❌ Toplu oluşturma hatası:', error);
            toast.error('Bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
            setIsSubmitting(false);
            setUploadProgress(0);
        }
    };

    // Form submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedMediaForBulk.length > 0) {
            await handleBulkSubmit();
        } else {
            toast.error('Lütfen en az bir medya seçin');
        }
    };

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && e.target.className === 'content-modal-backdrop') {
            onClose();
        }
    };

    const selectedPlatformCount = Object.values(platformSettings).filter(v => v).length;

    return (
        <>
            <LoadingOverlay
                isLoading={isSubmitting}
                uploadProgress={uploadProgress}
                editingContentId={false}
                mediaFiles={mediaFiles}
            />

            {showWatermark && watermarkingIndex !== null && (
                <MediaWatermark
                    isOpen={showWatermark}
                    onClose={() => {
                        setShowWatermark(false);
                        setWatermarkingIndex(null);
                    }}
                    mediaFile={mediaFiles[watermarkingIndex]}
                    mediaPreview={mediaPreviews[watermarkingIndex]?.url}
                    onSave={handleWatermarkSave}
                />
            )}

            {/* BULK VIDEO WATERMARK MODAL */}
            {showBulkWatermark && (
                <BulkVideoWatermark
                    isOpen={showBulkWatermark}
                    onClose={() => setShowBulkWatermark(false)}
                    videoPreviews={
                        selectedMediaForBulk.length > 0
                            ? selectedMediaForBulk.map(idx => mediaPreviews[idx]).filter(p => p?.type === 'video')
                            : mediaPreviews.filter(p => p?.type === 'video')
                    }
                    onSave={handleBulkWatermarkSave}
                />
            )}

            {/* Kaydedilmiş Ayarlar Bildirimi */}
            {showSavedSettings && savedSettings && (
                <div className="saved-settings-notification">
                    <div className="notification-content">
                        <span className="notification-icon">💾</span>
                        <span>Kaydedilmiş ayarlarınız var. Yüklemek ister misiniz?</span>
                        <button onClick={() => {
                            applySettings(savedSettings);
                            setShowSavedSettings(false);
                            toast.success('Kaydedilmiş ayarlar yüklendi!');
                        }} className="btn-load-settings">Yükle</button>
                        <button onClick={() => setShowSavedSettings(false)} className="btn-dismiss">Yoksay</button>
                        <button onClick={clearSavedSettings} className="btn-clear-settings">Temizle</button>
                    </div>
                </div>
            )}

            <div className="content-modal-backdrop" onMouseDown={handleBackdropClick}>
                <div className="content-modal-container" onMouseDown={(e) => e.stopPropagation()}>
                    {/* HEADER */}
                    <div className="content-modal-header">
                        <h2>
                            <span>⚡</span> Hızlı İçerik Oluştur
                            {selectedMediaForBulk.length > 0 && (
                                <span className="bulk-count-badge">
                                    {selectedMediaForBulk.length} içerik oluşturulacak
                                </span>
                            )}
                        </h2>
                        <button className="modal-close-btn" onClick={onClose}>✕</button>
                    </div>

                    {/* TAB NAVIGATION */}
                    <div className="content-modal-tabs">
                        <button
                            className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
                            onClick={() => setActiveTab('media')}
                        >
                            <span>🖼️</span> Medya
                            {mediaPreviews.length > 0 && <span className="badge">{mediaPreviews.length}</span>}
                            {selectedMediaForBulk.length > 0 && (
                                <span className="badge selected">{selectedMediaForBulk.length}</span>
                            )}
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'social' ? 'active' : ''}`}
                            onClick={() => setActiveTab('social')}
                        >
                            <span>📱</span> Sosyal Medya
                            {selectedPlatformCount > 0 && <span className="badge">{selectedPlatformCount}</span>}
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'autogram' ? 'active' : ''}`}
                            onClick={() => setActiveTab('autogram')}
                        >
                            <span>⚙️</span> Autogram Ayarları
                            {selectedTagIds.length > 0 && <span className="badge">{selectedTagIds.length}</span>}
                        </button>
                    </div>

                    {/* SAVE SETTINGS BUTTON */}
                    {activeTab !== 'media' && (
                        <div className="save-settings-bar">
                            <button
                                type="button"
                                onClick={saveCurrentSettings}
                                className="btn-save-settings"
                            >
                                <span>💾</span> Bu Ayarları Kaydet
                            </button>
                            {savedSettings && (
                                <span className="saved-date">
                                    Son kayıt: {new Date(savedSettings.savedAt).toLocaleString('tr-TR')}
                                </span>
                            )}
                        </div>
                    )}

                    {/* MODAL BODY */}
                    <div className="content-modal-body">
                        <form onSubmit={handleSubmit} id="content-form">
                            {activeTab === 'media' && renderBulkMediaTab()}

                            {activeTab === 'social' && (
                                <SocialMediaTab
                                    platformSettings={platformSettings}
                                    setPlatformSettings={setPlatformSettings}
                                    telegramSettings={telegramSettings}
                                    setTelegramSettings={setTelegramSettings}
                                    youtubeSettings={youtubeSettings}
                                    setYoutubeSettings={setYoutubeSettings}
                                    buttonForm={buttonForm}
                                    setButtonForm={setButtonForm}
                                    currentButton={currentButton}
                                    setCurrentButton={setCurrentButton}
                                    contentForm={contentForm}
                                    setContentForm={setContentForm}
                                    showEmojiPicker={showEmojiPicker}
                                    setShowEmojiPicker={setShowEmojiPicker}
                                    showLinkModal={showLinkModal}
                                    setShowLinkModal={setShowLinkModal}
                                    linkData={linkData}
                                    setLinkData={setLinkData}
                                    mediaPreviews={mediaPreviews}
                                    isEditMode={false}
                                    existingMedia={[]}
                                    xSettings={xSettings}
                                    setXSettings={setXSettings}
                                    tiktokSettings={tiktokSettings}
                                    setTiktokSettings={setTiktokSettings}
                                    instagramSettings={instagramSettings}
                                    setInstagramSettings={setInstagramSettings}
                                />
                            )}

                            {activeTab === 'autogram' && (
                                <AutogramSettingsTab
                                    contentForm={contentForm}
                                    setContentForm={setContentForm}
                                    selectedTagIds={selectedTagIds}
                                    setSelectedTagIds={setSelectedTagIds}
                                    tags={tags}
                                />
                            )}
                        </form>
                    </div>

                    {/* MODAL FOOTER */}
                    <div className="content-modal-footer">
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <div className="bulk-info">
                                {selectedMediaForBulk.length > 0 && (
                                    <span className="bulk-create-info">
                                        <span>⚡</span> {selectedMediaForBulk.length} adet içerik oluşturulacak
                                        {bulkWatermarkData && getSelectedVideoCount() > 0 && (
                                            <span style={{ marginLeft: '8px', color: '#667eea' }}>
                                                • 🎬 {getSelectedVideoCount()} videoya logo eklenecek
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="btn-cancel"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    form="content-form"
                                    className="btn-submit"
                                    disabled={isSubmitting || selectedMediaForBulk.length === 0}
                                >
                                    {selectedMediaForBulk.length > 0 ? (
                                        <><span>🚀</span> {selectedMediaForBulk.length} İçerik Oluştur</>
                                    ) : (
                                        <><span>➕</span> Medya Seçin</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default QuickContentModal;